"use client"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Tooltip,
  Box,
  SkeletonCircle,
  SkeletonText,
  Input,
  Checkbox,
  Switch,
  Textarea,
  useToast,
  Badge,
  Alert,
  AlertIcon, Menu, MenuButton, MenuItem, InputGroup, InputLeftElement, Spinner,
  MenuList
} from "@chakra-ui/react"
import {
  candidateExpAsync,
  deleteExpAsyn,
  selectFetchedExperience,
} from "../../CandProfCreation/CandidateProfileSlice/Experience/ExperienceSlice"
import { fetchToken } from "../../common/FetchCookie"
import { selectToken } from "../../common/FetchTokenSlice"
import {
  updateCandidateExperienceAsync,
  addExperienceAsync,
} from "../../CandProfCreation/CandidateProfileSlice/Experience/ExperienceSlice2"
import { useForm } from "react-hook-form"
import {
  fetchJobDepartmentsAsync,
  selectJobDepartments,
} from "../../AdminPanel/JobPostingSlice/JobDepartment/JobDepartmentSlice"
import { fetchPublicJobTitlesAsync, selectJobTitles } from "../../AdminPanel/JobPostingSlice/JobTitles/JobTitlesSlice"
import EmptyState from "../../CandProfCreation/components/Tester"
import { FaBriefcase } from "react-icons/fa"

export default function ExperienceShowTesting() {
  const dispatch = useDispatch()
  const [selectedExperience, setSelectedExperience] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasOffer, setHasOffer] = useState(false)
  const [deletingExperience, setDeletingExperience] = useState(null)
  const [isUpdate, setIsUpdate] = useState(false)
  const [jobTitleSearch, setJobTitleSearch] = useState("")
  const [selectedJobTitleLabel, setSelectedJobTitleLabel] = useState("")
  const [offeredSelectedJobTitleLabel, setOfferedSelectedJobTitleLabel] = useState("")
  const jobTitlesLoading = useSelector((state) => state.jobTitles?.loading) // uses same slice
  const toast = useToast()

  const hasFetchedExperience = useRef(false)
  const hasFetchedDepartments = useRef(false)
  const hasFetchedJobTitles = useRef(false)
  const hasInitiatedFetch = useRef(false)

  const experience = useSelector(selectFetchedExperience)
  const department = useSelector(selectJobDepartments)
  const jobTitle = useSelector(selectJobTitles)
  const token = useSelector(selectToken)

  const currentYear = new Date().getFullYear()
  const minValidYear = currentYear - 50
  const years = Array.from({ length: 51 }, (_, i) => currentYear - i)
  const months = Array.from({ length: 12 }, (_, i) => i + 1)

  const {
    isOpen: isExperienceModalOpen,
    onOpen: onExperienceModalOpen,
    onClose: onExperienceModalClose,
  } = useDisclosure()
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      is_current_employement: false,
      experience_year: 0,
      experience_month: 0,
      start_year: currentYear,
      start_month: 1,
      end_year: currentYear,
      end_month: 1,
    },
  })

  useEffect(() => {
    if (!hasInitiatedFetch.current) {
      hasInitiatedFetch.current = true
      if (!experience || experience.length === 0) {
        fetchExperience()
      } else {
        setIsLoading(false)
      }
      if (!department || department.length === 0) {
        dispatch(fetchJobDepartmentsAsync())
      }
      if (!jobTitle || jobTitle.length === 0) {
        dispatch(fetchPublicJobTitlesAsync())
      }
    }
  }, [dispatch, experience, department, jobTitle])

  const isCurrentEmployment = watch("is_current_employement")

  useEffect(() => {
    if (isCurrentEmployment) {
      setValue("end_year", undefined)
      setValue("end_month", undefined)
    }
  }, [isCurrentEmployment, setValue])
  useEffect(() => {
    if (isExperienceModalOpen) {
      // get initial 10 without a keyword
      dispatch(fetchPublicJobTitlesAsync({ keyword: "" }))
    }
  }, [isExperienceModalOpen, dispatch])

  useEffect(() => {
    if (!isExperienceModalOpen) return
    const t = setTimeout(() => {
      dispatch(fetchPublicJobTitlesAsync({ keyword: jobTitleSearch.trim() }))
    }, 300) // debounce
    return () => clearTimeout(t)
  }, [jobTitleSearch, isExperienceModalOpen, dispatch])

  const validateMonth = (value) => {
    if (!value) return "Month is required"
    const month = Number.parseInt(value)
    return (month >= 1 && month <= 12) || "Month must be between 1 and 12"
  }

  const validateStartYear = (value) => {
    if (!value) return "Start year is required"
    const year = Number.parseInt(value)
    return (year >= minValidYear && year <= currentYear) || `Year must be between ${minValidYear} and ${currentYear}`
  }

  const validateEndYear = (value) => {
    if (isCurrentEmployment) return true
    if (!value) return "End year is required"
    const startYear = Number.parseInt(getValues("start_year"))
    const endYear = Number.parseInt(value)
    if (endYear < startYear) {
      return "End year must be greater than or equal to start year"
    }
    return (
      (endYear >= minValidYear && endYear <= currentYear) || `Year must be between ${minValidYear} and ${currentYear}`
    )
  }

  const validateEndMonth = (value) => {
    if (isCurrentEmployment) return true
    if (!value) return "End month is required"
    const month = Number.parseInt(value)
    if (month < 1 || month > 12) {
      return "Month must be between 1 and 12"
    }
    const startYear = Number.parseInt(getValues("start_year"))
    const endYear = Number.parseInt(getValues("end_year"))
    const startMonth = Number.parseInt(getValues("start_month"))
    if (startYear === endYear && month < startMonth) {
      return "End month must be greater than or equal to start month when years are the same"
    }
    return true
  }

  const calculateExperience = (startYear, startMonth, endYear, endMonth) => {
    let totalMonths = 0
    if (!endYear || !endMonth) {
      const currentDate = new Date()
      totalMonths = (currentDate.getFullYear() - startYear) * 12 + (currentDate.getMonth() + 1 - startMonth)
    } else {
      totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth)
    }
    totalMonths = Math.max(0, totalMonths)
    return {
      years: Math.floor(totalMonths / 12),
      months: totalMonths % 12,
    }
  }

  const fetchExperience = async () => {
    setIsLoading(true)
    try {
      const fetchedToken = await fetchToken()
      await dispatch(candidateExpAsync(fetchedToken))
    } catch (error) {
      console.error("Error fetching experience:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const openAddModal = () => {
    reset({
      is_current_employement: false,
      experience_year: 0,
      experience_month: 0,
      start_year: currentYear,
      start_month: 1,
      end_year: currentYear,
      end_month: 1,
    })
    setHasOffer(false)
    setIsUpdate(false)
    setSelectedExperience(null)
    setJobTitleSearch("")
    setSelectedJobTitleLabel("")
    setOfferedSelectedJobTitleLabel("")
    onExperienceModalOpen()
  }

  const openUpdateModal = (experience) => {
    if (!experience) {
      console.log("No experience data provided")
      return
    }
    reset()
    setSelectedExperience(experience)
    setIsUpdate(true)

    const isCurrentEmployment = experience.is_current_employement === 1 || experience.is_current_employement === true
    setValue("is_current_employement", isCurrentEmployment)
    Object.keys(experience).forEach((key) => {
      if (experience[key] !== undefined && experience[key] !== null) {
        setValue(key, experience[key])
      }
    })
    if (!experience.job_title_uuid && experience.job_title) {
      setValue("job_title_uuid", experience.job_title.uuid)
    }
    if (!experience.department_uuid && experience.department) {
      setValue("department_uuid", experience.department.uuid)
    }
    if (experience.offered_job_title) {
      setValue("offered_job_title_uuid", experience.offered_job_title.uuid)
    }
    if (!isCurrentEmployment) {
      if (experience.end_year) setValue("end_year", experience.end_year)
      if (experience.end_month) setValue("end_month", experience.end_month)
    } else {
      setValue("end_year", undefined)
      setValue("end_month", undefined)
    }
    setHasOffer(experience.has_any_offer || false)

    // set labels for the menu buttons
    setSelectedJobTitleLabel(experience.job_title?.title || "")
    setOfferedSelectedJobTitleLabel(experience.offered_job_title?.title || "")

    onExperienceModalOpen()
  }

  const openDeleteModal = (experience) => {
    setDeletingExperience(experience)
    onDeleteModalOpen()
  }

  const validateAndSubmitForm = (data) => {
    const { years: calculatedYears, months: calculatedMonths } = calculateExperience(
      data.start_year,
      data.start_month,
      isCurrentEmployment ? null : data.end_year,
      isCurrentEmployment ? null : data.end_month,
    )
    if (data.experience_year !== calculatedYears || data.experience_month !== calculatedMonths) {
      toast({
        title: "Experience mismatch",
        description: `Based on your dates, your experience should be ${calculatedYears} years and ${calculatedMonths} months.`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      })
      setValue("experience_year", calculatedYears)
      setValue("experience_month", calculatedMonths)
      if (
        !window.confirm("Your experience doesn't match the dates provided. We've updated it. Do you want to continue?")
      ) {
        return
      }
      data.experience_year = calculatedYears
      data.experience_month = calculatedMonths
    }
    if (data.is_current_employement) {
      data.end_year = null
      data.end_month = null
    }
    if (isUpdate) {
      handleUpdate(data)
    } else {
      handleAdd(data)
    }
  }

  const handleAdd = async (data) => {
    try {
      const response = await dispatch(addExperienceAsync({ userData: data }))
      onExperienceModalClose()
      fetchExperience()
      toast({
        title: "Experience saved.",
        description: "Your experience details have been successfully saved.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to add experience:", error)
      toast({
        title: "Error",
        description: "Failed to add experience. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    }
  }

  const handleUpdate = async (data) => {
    if (selectedExperience) {
      try {
        const updatedData = {
          ...data,
          has_any_offer: hasOffer,
          last_working_date: hasOffer ? data.last_working_date : "",
          offered_job_title_uuid: hasOffer ? data.offered_job_title_uuid : null,
          offered_salary_currency: hasOffer ? data.offered_salary_currency : "",
          offered_salary_amount: hasOffer ? data.offered_salary_amount : null,
        }
        const response = await dispatch(
          updateCandidateExperienceAsync({
            uuid: selectedExperience.uuid,
            CandidateExperienceData: updatedData,
          }),
        ).unwrap()
        if (response?.status === "success") {
          toast({
            title: "Experience updated",
            description: "Your experience details have been successfully updated.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          })
          onExperienceModalClose()
          fetchExperience()
        } else {
          toast({
            title: "Update failed",
            description: response?.message || "Something went wrong.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          })
        }
      } catch (error) {
        console.error("Failed to update experience:", error)
        toast({
          title: "Error",
          description: "Failed to update experience. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      }
    }
  }

  const handleDelete = async () => {
    if (deletingExperience && token) {
      try {
        const response = await dispatch(deleteExpAsyn({ token, uuid: deletingExperience.uuid }))
        if (response?.payload?.status === "success") {
          toast({
            title: "Experience Deleted",
            description: "Your experience details have been successfully deleted.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          })
        } else {
          toast({
            title: "Deletion Failed",
            description: response?.payload?.message || "Failed to delete experience. Please try again.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          })
        }
        onDeleteModalClose()
        fetchExperience()
      } catch (error) {
        console.error("Failed to delete experience:", error)
        toast({
          title: "Error",
          description: "Failed to delete experience. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      }
    }
  }

  return (
    <div className="">
      <div className="bg-mainBg " style={{ fontFamily: "Inter, sans-serif" }}>
        <div className=" mx-auto  ">
          <div className="bg-white rounded-xl w-full px-3 sm:px-5 py-4 shadow-sm  border-gray-100">
            {/* Header */}
            <div className="flex sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-start gap-2">
                <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FaBriefcase />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl text-left font-semibold">Work Experience</h1>
                  <p className="text-xs text-gray-500">Add and manage your professional experience</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Tooltip label="Add new experience" aria-label="Add experience tooltip">
                  <Button onClick={openAddModal} colorScheme="blue" className="whitespace-nowrap shadow">
                    Add <i className="fa-solid fa-plus pl-2"></i>
                  </Button>
                </Tooltip>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-3">
              {isLoading ? (
                <>
                  <Box className="border p-4 sm:p-5 bg-white shadow-sm rounded-lg">
                    <SkeletonCircle size="14" mb="4" />
                    <SkeletonText noOfLines={4} spacing="3" skeletonHeight="3" />
                  </Box>
                  <Box className="border p-4 sm:p-5 bg-white shadow-sm rounded-lg">
                    <SkeletonCircle size="14" mb="4" />
                    <SkeletonText noOfLines={4} spacing="3" skeletonHeight="3" />
                  </Box>
                </>
              ) : Array.isArray(experience) && experience.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6">
                  <EmptyState
                    title="Experience"
                    icon={FaBriefcase}
                    emptyMessage="Start by adding your Experience"
                    addButtonText="Add Your Experience"
                    onAdd={() => {
                      openAddModal()
                    }}
                  />
                </div>
              ) : (
                experience &&
                experience.length > 0 &&
                experience.map((item, index) => {
                  const isCurrent =
                    item.is_current_employement === 1 || item.is_current_employement === true || item.end_year === null
                  return (
                    <div
                      className="group relative border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 bg-white overflow-hidden"
                      key={index}
                    >
                      {/* Accent bar */}
                      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-indigo-500" />

                      <div className="p-4 md:p-5">
                        {/* Header + actions */}
                        <div className="flex justify-between items-start gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight break-words">
                                {item.job_title ? item.job_title.title : item.job_profile}
                              </h2>
                              {isCurrent && (
                                <Badge colorScheme="green" variant="subtle" className="rounded-full">
                                  Current
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm sm:text-base text-indigo-600 capitalize font-semibold break-words">
                              {item.current_company}
                            </p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Tooltip label="Edit experience" aria-label="Edit experience tooltip">
                              <Button
                                onClick={() => openUpdateModal(item)}
                                aria-label="Edit experience"
                                colorScheme="blue"
                                className="h-10 w-10 p-0 flex items-center justify-center shadow-sm"
                              >
                                <i className="fa-sharp fa-solid fa-pen"></i>
                              </Button>
                            </Tooltip>
                            <Tooltip label="Delete experience" aria-label="Delete experience tooltip">
                              <Button
                                onClick={() => openDeleteModal(item)}
                                aria-label="Delete experience"
                                colorScheme="red"
                                variant="outline"
                                className="h-10 w-10 p-0 flex items-center justify-center"
                              >
                                <i className="cursor-pointer fa-solid fa-trash"></i>
                              </Button>
                            </Tooltip>
                          </div>
                        </div>

                        {/* Meta badges */}
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
                          {item.department?.title && (
                            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-gray-700">
                              {item.department.title}
                            </span>
                          )}
                          {item.employement_type && (
                            <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-amber-700">
                              {item.employement_type}
                            </span>
                          )}
                          {item.notice_period && (
                            <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-indigo-700">
                              {item.notice_period}
                            </span>
                          )}
                        </div>

                        {/* Details */}
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center flex-wrap text-gray-700 text-sm">
                            <i className="fa-solid fa-calendar-days text-gray-500 w-6"></i>
                            <span className="font-medium mr-1">Total Experience:</span>
                            <span>
                              {item.experience_year} Years{typeof item.experience_month === "number" ? ` ${item.experience_month} Months` : ""}
                            </span>
                          </div>

                          <div className="flex items-center flex-wrap text-gray-700 text-sm">
                            <i className="fa-solid fa-clock text-gray-500 w-6"></i>
                            <span className="font-medium mr-1">Duration:</span>
                            <span>
                              {item.start_month
                                ? new Date(0, item.start_month - 1).toLocaleString("default", { month: "short" })
                                : ""}
                              {item.start_year ? ` ${item.start_year}` : ""} -{" "}
                              {isCurrent
                                ? "Present"
                                : `${item.end_month ? new Date(0, item.end_month - 1).toLocaleString("default", { month: "short" }) : ""} ${item.end_year || ""
                                }`}
                            </span>
                          </div>

                          <div className="flex items-start text-gray-700 text-sm">
                            <i className="fa-solid fa-list-check text-gray-500 w-6 mt-0.5"></i>
                            <div className="flex-1">
                              <span className="font-medium mr-1">Key Responsibility Area:</span>
                              <span className="break-words">{item.job_profile}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Single Experience Modal for Add/Update */}
      <Modal isOpen={isExperienceModalOpen} onClose={onExperienceModalClose}>
        <ModalOverlay />
        <ModalContent maxW="5xl">
          <form onSubmit={handleSubmit(validateAndSubmitForm)}>
            <ModalHeader>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FaBriefcase />
                </div>
                <span>{isUpdate ? "Update Experience" : "Add Experience"}</span>
              </div>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <FormControl>
                <FormLabel>Current Employment</FormLabel>
                <Switch
                  {...register("is_current_employement")}
                  colorScheme="blue"
                  isChecked={watch("is_current_employement")}
                  onChange={(e) => {
                    setValue("is_current_employement", e.target.checked)
                    if (e.target.checked) {
                      setValue("end_year", undefined)
                      setValue("end_month", undefined)
                    }
                  }}
                />
              </FormControl>

              <FormControl mt={4} isRequired>
                <FormLabel>Current Company</FormLabel>
                <Input
                  {...register("current_company", {
                    required: "Company name is required",
                  })}
                  placeholder="Enter company name"
                />
                {errors.current_company && (
                  <p className="text-red-500 text-sm mt-1">{errors.current_company.message}</p>
                )}
              </FormControl>

              <FormControl mt={4} isRequired>
                <FormLabel>Job Department</FormLabel>
                <Select
                  {...register("department_uuid", {
                    required: "Department is required",
                  })}
                  placeholder="Select Job Department"
                >
                  {Array.isArray(department) &&
                    department.map((job) => (
                      <option key={job.id} value={job.uuid}>
                        {job.title}
                      </option>
                    ))}
                </Select>
                {errors.department_uuid && (
                  <p className="text-red-500 text-sm mt-1">{errors.department_uuid.message}</p>
                )}
              </FormControl>

              <FormControl mt={4} isRequired>
                <FormLabel>Designation</FormLabel>

                {/* keep react-hook-form field via hidden input to preserve validation logic */}
                <input
                  type="hidden"
                  {...register("job_title_uuid", {
                    required: "Designation is required",
                  })}
                />

                <Menu closeOnSelect>
                  <MenuButton className="text-left font-normal" as={Button} variant="outline" w="100%" rightIcon={<i className="fa-solid fa-chevron-down"></i>}>
                    {selectedJobTitleLabel ||
                      (Array.isArray(jobTitle)
                        ? jobTitle.find((j) => j.uuid === watch("job_title_uuid"))?.title
                        : "") ||
                      "Select Designation"}
                  </MenuButton>

                  <MenuList maxH="320px" overflowY="auto" minW="sm">
                    <Box p={3} borderBottom="1px solid" borderColor="gray.100">
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <i className="fa-solid fa-magnifying-glass text-gray-400"></i>
                        </InputLeftElement>
                        <Input
                          placeholder="Search designation..."
                          value={jobTitleSearch}
                          onChange={(e) => setJobTitleSearch(e.target.value)}
                          autoFocus
                        />
                      </InputGroup>
                    </Box>

                    {jobTitlesLoading ? (
                      <Box p={3} textAlign="center" display="flex" alignItems="center" justifyContent="center" gap={2}>
                        <Spinner size="sm" /> Loading...
                      </Box>
                    ) : Array.isArray(jobTitle) && jobTitle.length > 0 ? (
                      jobTitle.map((item) => (
                        <MenuItem
                          key={item.uuid}
                          onClick={() => {
                            setValue("job_title_uuid", item.uuid, { shouldValidate: true })
                            setSelectedJobTitleLabel(item.title)
                          }}
                        >
                          {item.title}
                        </MenuItem>
                      ))
                    ) : (
                      <Box p={3} color="gray.500">
                        No results
                      </Box>
                    )}
                  </MenuList>
                </Menu>

                {errors.job_title_uuid && <p className="text-red-500 text-sm mt-1">{errors.job_title_uuid.message}</p>}
              </FormControl>

              <FormControl mt={4} isRequired>
                <FormLabel>Employment Type</FormLabel>
                <Select
                  {...register("employement_type", {
                    required: "Employment type is required",
                  })}
                  placeholder="Select Employment Type"
                >
                  <option value="Full Time">Full Time</option>
                  <option value="Part Time">Part Time</option>
                  <option value="Contract">Contract</option>
                </Select>
                {errors.employement_type && (
                  <p className="text-red-500 text-sm mt-1">{errors.employement_type.message}</p>
                )}
              </FormControl>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <FormControl isRequired>
                  <FormLabel>Start Year</FormLabel>
                  <Select
                    {...register("start_year", {
                      required: "Start year is required",
                      validate: validateStartYear,
                      valueAsNumber: true,
                    })}
                    placeholder="Select Start Year"
                    onChange={(e) => {
                      const startYear = Number.parseInt(e.target.value)
                      const startMonth = getValues("start_month") || 1
                      const endYear = isCurrentEmployment ? null : getValues("end_year")
                      const endMonth = isCurrentEmployment ? null : getValues("end_month")
                      if (startYear && startMonth) {
                        const { years, months } = calculateExperience(startYear, startMonth, endYear, endMonth)
                        setValue("experience_year", years)
                        setValue("experience_month", months)
                      }
                      if (!isCurrentEmployment) {
                        trigger(["end_year", "end_month"])
                      }
                    }}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                  {errors.start_year && <p className="text-red-500 text-sm mt-1">{errors.start_year.message}</p>}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Start Month</FormLabel>
                  <Select
                    {...register("start_month", {
                      required: "Start month is required",
                      validate: validateMonth,
                      valueAsNumber: true,
                    })}
                    placeholder="Select Start Month"
                    onChange={(e) => {
                      const startMonth = Number.parseInt(e.target.value)
                      const startYear = getValues("start_year")
                      const endYear = isCurrentEmployment ? null : getValues("end_year")
                      const endMonth = isCurrentEmployment ? null : getValues("end_month")
                      if (startYear && startMonth) {
                        const { years, months } = calculateExperience(startYear, startMonth, endYear, endMonth)
                        setValue("experience_year", years)
                        setValue("experience_month", months)
                      }
                      if (!isCurrentEmployment) {
                        trigger(["end_month"])
                      }
                    }}
                  >
                    {months.map((month) => (
                      <option key={month} value={month}>
                        {new Date(0, month - 1).toLocaleString("default", { month: "long" })}
                      </option>
                    ))}
                  </Select>
                  {errors.start_month && <p className="text-red-500 text-sm mt-1">{errors.start_month.message}</p>}
                </FormControl>
              </div>

              {!isCurrentEmployment && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <FormControl isRequired>
                    <FormLabel>End Year</FormLabel>
                    <Select
                      {...register("end_year", {
                        required: !isCurrentEmployment ? "End year is required" : false,
                        validate: validateEndYear,
                        valueAsNumber: true,
                        disabled: isCurrentEmployment,
                      })}
                      placeholder="Select End Year"
                      onChange={(e) => {
                        trigger("end_month")
                        const endYear = Number.parseInt(e.target.value)
                        const endMonth = getValues("end_month") || 1
                        const startYear = getValues("start_year")
                        const startMonth = getValues("start_month")
                        if (startYear && startMonth && endYear && endMonth) {
                          const { years, months } = calculateExperience(startYear, startMonth, endYear, endMonth)
                          setValue("experience_year", years)
                          setValue("experience_month", months)
                        }
                      }}
                      isDisabled={isCurrentEmployment}
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Select>
                    {errors.end_year && <p className="text-red-500 text-sm mt-1">{errors.end_year.message}</p>}
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>End Month</FormLabel>
                    <Select
                      {...register("end_month", {
                        required: !isCurrentEmployment ? "End month is required" : false,
                        validate: validateEndMonth,
                        valueAsNumber: true,
                        disabled: isCurrentEmployment,
                      })}
                      placeholder="Select End Month"
                      onChange={(e) => {
                        const endMonth = Number.parseInt(e.target.value)
                        const endYear = getValues("end_year")
                        const startYear = getValues("start_year")
                        const startMonth = getValues("start_month")
                        if (startYear && startMonth && endYear && endMonth) {
                          const { years, months } = calculateExperience(startYear, startMonth, endYear, endMonth)
                          setValue("experience_year", years)
                          setValue("experience_month", months)
                        }
                      }}
                      isDisabled={isCurrentEmployment}
                    >
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {new Date(0, month - 1).toLocaleString("default", { month: "long" })}
                        </option>
                      ))}
                    </Select>
                    {errors.end_month && <p className="text-red-500 text-sm mt-1">{errors.end_month.message}</p>}
                  </FormControl>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mt-4">
                <FormControl isRequired>
                  <FormLabel>Experience (Years)</FormLabel>
                  <Select
                    {...register("experience_year", {
                      required: "Years is required",
                      min: { value: 0, message: "Cannot be negative" },
                      valueAsNumber: true,
                    })}
                    placeholder="Select Years"
                    isReadOnly
                  >
                    {Array.from({ length: 31 }, (_, i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </Select>
                  {errors.experience_year && (
                    <p className="text-red-500 text-sm mt-1">{errors.experience_year.message}</p>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Experience (Months)</FormLabel>
                  <Select
                    {...register("experience_month", {
                      required: "Months is required",
                      min: { value: 0, message: "Cannot be negative" },
                      max: { value: 11, message: "Must be less than 12" },
                      valueAsNumber: true,
                    })}
                    placeholder="Select Months"
                    isReadOnly
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </Select>
                  {errors.experience_month && (
                    <p className="text-red-500 text-sm mt-1">{errors.experience_month.message}</p>
                  )}
                </FormControl>
              </div>

              <FormControl mt={4} isRequired>
                <FormLabel>Current Salary Currency</FormLabel>
                <Select
                  {...register("current_salary_currency", {
                    required: "Currency is required",
                  })}
                  defaultValue="INR"
                  placeholder="Select currency"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </Select>
                {errors.current_salary_currency && (
                  <p className="text-red-500 text-sm mt-1">{errors.current_salary_currency.message}</p>
                )}
              </FormControl>

              <div className="flex gap-2 mt-4">
                <FormControl isRequired>
                  <FormLabel>Current CTC</FormLabel>
                  <Input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    {...register("current_salary_amount", {
                      required: "Salary amount is required",
                      min: { value: 0, message: "Cannot be negative" },
                      valueAsNumber: true,
                    })}
                    placeholder="Enter amount"
                  />
                  {errors.current_salary_amount && (
                    <p className="text-red-500 text-sm mt-1">{errors.current_salary_amount.message}</p>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Pay Type</FormLabel>
                  <Select
                    {...register("current_pay_type", {
                      required: "Pay Type is required",
                    })}
                    placeholder="Select Pay Type"
                  >
                    <option value="Per Month">Per Month</option>
                    <option value="Per Year">Per Year</option>
                  </Select>
                  {errors.current_pay_type && (
                    <p className="text-red-500 text-sm mt-1">{errors.current_pay_type.message}</p>
                  )}
                </FormControl>
              </div>

              <FormControl mt={4} isRequired>
                <FormLabel>Key Responsibility Area</FormLabel>
                <Textarea
                  {...register("job_profile", {
                    required: "Responsibility area is required",
                  })}
                  placeholder="Enter Key Responsibility Area"
                />
                {errors.job_profile && <p className="text-red-500 text-sm mt-1">{errors.job_profile.message}</p>}
              </FormControl>

              <FormControl mt={4} isRequired>
                <FormLabel>Notice Period</FormLabel>
                <Select
                  placeholder="Notice Period"
                  {...register("notice_period", {
                    required: "Notice Period is required",
                  })}
                >
                  <option value={"Immediate Join"}>Immediate Join</option>
                  <option value={"15 Days or less"}>15 Days or less</option>
                  <option value={"1 month"}>1 month</option>
                  <option value={"2 months"}>2 months</option>
                  <option value={"3 months"}>3 months</option>
                  <option value={"More than 3 months"}>More than 3 months</option>
                  <option value={"Serving Notice Period"}>Serving Notice Period</option>
                </Select>
                {errors.notice_period && <p className="text-red-500 text-sm mt-1">{errors.notice_period.message}</p>}
              </FormControl>

              <FormControl mt={4}>
                <Checkbox isChecked={hasOffer} onChange={(e) => setHasOffer(e.target.checked)}>
                  Do you have any offer?
                </Checkbox>
              </FormControl>

              {hasOffer && (
                <>
                  <FormControl mt={4} isRequired>
                    <FormLabel>Last Working Date</FormLabel>
                    <Input
                      type="date"
                      {...register("last_working_date", {
                        required: hasOffer ? "Last working date is required" : false,
                      })}
                    />
                    {errors.last_working_date && (
                      <p className="text-red-500 text-sm mt-1">{errors.last_working_date.message}</p>
                    )}
                  </FormControl>

                  <FormControl mt={4} isRequired>
                    <FormLabel>Offered Designation</FormLabel>

                    <input
                      type="hidden"
                      {...register("offered_job_title_uuid", {
                        required: hasOffer ? "Offered designation is required" : false,
                      })}
                    />

                    <Menu closeOnSelect>
                      <MenuButton className="text-left font-normal" as={Button} variant="outline" w="100%" rightIcon={<i className="fa-solid fa-chevron-down"></i>}>
                        {offeredSelectedJobTitleLabel ||
                          (Array.isArray(jobTitle)
                            ? jobTitle.find((j) => j.uuid === watch("offered_job_title_uuid"))?.title
                            : "") ||
                          "Select Offered Designation"}
                      </MenuButton>

                      <MenuList maxH="320px" overflowY="auto" minW="sm">
                        <Box p={3} borderBottom="1px solid" borderColor="gray.100">
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <i className="fa-solid fa-magnifying-glass text-gray-400"></i>
                            </InputLeftElement>
                            <Input
                              placeholder="Search designation..."
                              value={jobTitleSearch}
                              onChange={(e) => setJobTitleSearch(e.target.value)}
                              autoFocus
                              className="text-left"
                            />
                          </InputGroup>
                        </Box>

                        {jobTitlesLoading ? (
                          <Box p={3} textAlign="center" display="flex" alignItems="center" justifyContent="center" gap={2}>
                            <Spinner size="sm" /> Loading...
                          </Box>
                        ) : Array.isArray(jobTitle) && jobTitle.length > 0 ? (
                          jobTitle.map((item) => (
                            <MenuItem
                              key={item.uuid}
                              onClick={() => {
                                setValue("offered_job_title_uuid", item.uuid, { shouldValidate: true })
                                setOfferedSelectedJobTitleLabel(item.title)
                              }}
                            >
                              {item.title}
                            </MenuItem>
                          ))
                        ) : (
                          <Box p={3} color="gray.500">
                            No results
                          </Box>
                        )}
                      </MenuList>
                    </Menu>

                    {/* Note: your original error key was "offered_job_title_id", keeping your validation it should be "offered_job_title_uuid" */}
                    {errors.offered_job_title_uuid && (
                      <p className="text-red-500 text-sm mt-1">{errors.offered_job_title_uuid.message}</p>
                    )}
                  </FormControl>

                  <FormControl mt={4} isRequired>
                    <FormLabel>Offered Salary Currency</FormLabel>
                    <Select
                      {...register("offered_salary_currency", {
                        required: "Offered Salary Currency is required",
                      })}
                      defaultValue="INR"
                      placeholder="Select currency"
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                    </Select>
                    {errors.offered_salary_currency && (
                      <p className="text-red-500 text-sm mt-1">{errors.offered_salary_currency.message}</p>
                    )}
                  </FormControl>

                  <div className="flex gap-2 mt-4">
                    <FormControl isRequired>
                      <FormLabel>Offered CTC</FormLabel>
                      <Input
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        {...register("offered_salary_amount", {
                          required: hasOffer ? "Offered salary amount is required" : false,
                          min: { value: 0, message: "Cannot be negative" },
                          valueAsNumber: true,
                        })}
                        placeholder="Enter amount"
                      />
                      {errors.offered_salary_amount && (
                        <p className="text-red-500 text-sm mt-1">{errors.offered_salary_amount.message}</p>
                      )}
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Pay Type</FormLabel>
                      <Select
                        {...register("offered_pay_type", {
                          required: "Pay Type is required",
                        })}
                        placeholder="Select Pay Type"
                      >
                        <option value="Per Month">Per Month</option>
                        <option value="Per Year">Per Year</option>
                      </Select>
                      {errors.offered_pay_type && (
                        <p className="text-red-500 text-sm mt-1">{errors.offered_pay_type.message}</p>
                      )}
                    </FormControl>
                  </div>
                </>
              )}
            </ModalBody>
            <ModalFooter className="bg-logoblue">
              <Button mr={3} type="submit">
                {isUpdate ? "Update" : "Save"}
              </Button>
              <Button onClick={onExperienceModalClose}>Cancel</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Experience Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent padding={3}>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton color="gray.500" />
          <ModalBody>
            <Alert status="warning" mb={3} borderRadius="md" variant="left-accent">
              <AlertIcon />
              This action cannot be undone.
            </Alert>
            <div className="">
              <h4 className="text-sm sm:text-base text-gray-800">
                Are you sure you want to delete the
                <span className="font-semibold"> {deletingExperience?.job_title?.title} </span>
                experience?
              </h4>
            </div>
          </ModalBody>
          <ModalFooter gap={4}>
            <Button colorScheme="red" onClick={handleDelete}>
              Delete
            </Button>
            <Button variant="outline" onClick={onDeleteModalClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}